import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OperonLogo } from "@/components/OperonLogo";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { Sun, Moon, Upload, ArrowRight } from "lucide-react";

export default function Register() {
  const { theme, toggleTheme } = useTheme();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    email: "",
    password: "",
    theme: "dark",
    full_name: "",
    custom_logo_base64: null,
  });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo must be under 500 KB");
      return;
    }
    const r = new FileReader();
    r.onload = () => update("custom_logo_base64", r.result);
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to Operon! Your workspace is ready.");
      navigate("/app/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2">
      <div className="hidden lg:flex items-center justify-center relative overflow-hidden bg-card">
        <div className="absolute inset-0 operon-navy-band" />
        <div className="relative p-12 max-w-md">
          <OperonLogo theme="dark" size={44} />
          <h2 className="mt-8 font-heading text-3xl font-semibold tracking-tight">Build your operations stack in minutes.</h2>
          <p className="mt-3 text-muted-foreground">Register your company to create an isolated workspace. Full feature access for 14 days — no credit card.</p>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Link to="/" className="flex items-center">
            <OperonLogo theme={theme} size={32} />
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">Create your workspace</h1>
              <p className="text-sm text-muted-foreground mt-1">14-day free trial with full access</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company_name">Company name</Label>
              <Input id="company_name" required value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="Acme Manufacturing" data-testid="register-company-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Your name</Label>
              <Input id="full_name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jane Doe" data-testid="register-fullname-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="owner@company.com" data-testid="register-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="At least 6 chars" data-testid="register-password-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Theme</Label>
              <div className="grid grid-cols-2 gap-2">
                {["dark", "light"].map((t) => (
                  <button type="button" key={t} onClick={() => update("theme", t)} className={`rounded-lg border px-3 py-2.5 text-sm font-medium capitalize transition-colors ${form.theme === t ? "border-primary ring-2 ring-primary/25 bg-primary/5" : "border-border hover:bg-accent/70"}`} data-testid={`register-theme-${t}`}>
                    {t} mode
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Company logo (optional)</Label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-accent/50">
                  <Upload className="w-4 h-4" />
                  <span>Upload PNG/SVG</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="register-logo-input" />
                </label>
                {form.custom_logo_base64 && (
                  <img src={form.custom_logo_base64} alt="preview" className="h-10 w-auto rounded border border-border bg-white p-1" />
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">Custom logos render in light mode. Default Operon logo in dark mode.</div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
              {loading ? "Creating workspace…" : <>Create workspace <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              Have an account? <Link to="/login" className="text-primary hover:underline" data-testid="register-to-login-link">Sign in</Link>
            </div>
            <div className="text-[11px] text-muted-foreground text-center">
              By creating an account you agree to our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
